<script lang="ts" setup>
const props = defineProps({
  columns: {
    default: () => ({}),
    type: Object as PropType<FieldColumnsMatcherColumns>,
  },
  hasPreview: {
    default: true,
    type: Boolean,
  },
  modelValue: {
    default: () => ({}),
    type: Object as PropType<FieldColumnsMatcherModelValue>,
  },
})

const emit = defineEmits<{
  'update:modelValue': [modelValue: FieldColumnsMatcherModelValue]
}>()

const { t } = useI18n()

const columnsMatched = ref<{ fileColumn?: string, schemaColumn?: string }[]>([])

const headers = computed(() => {
  const headers = [
    'name',
    'preview',
    'match',
    'actions',
  ]

  if (!props.hasPreview) {
    headers.splice(1, 1)
  }

  return headers.map((header) => {
    return {
      label: t(`headers.${header}`),
      value: header,
    }
  })
})

const schemaColumns = computed((): USelectMenuOption[] => {
  return [
    {
      label: t('options.choose'),
      value: 'choose',
    },
    {
      label: t('options.domain'),
      value: 'domain',
    },
    {
      label: t('options.name'),
      value: 'name',
    },
    {
      label: t('options.linkedin'),
      value: 'linkedin',
    },
    {
      label: t('options.dribbble'),
      value: 'dribbble',
    },
    {
      label: t('options.facebook'),
      value: 'facebook',
    },
    {
      label: t('options.github'),
      value: 'github',
    },
    {
      label: t('options.instagram'),
      value: 'instagram',
    },
    {
      label: t('options.snapchat'),
      value: 'snapchat',
    },
    {
      label: t('options.tiktok'),
      value: 'tiktok',
    },
    {
      label: t('options.twitter'),
      value: 'twitter',
    },
    {
      label: t('options.youtube'),
      value: 'youtube',
    },
    {
      label: t('options.pinterest'),
      value: 'pinterest',
    },
  ]
})

function onMoreColumns() {
  columnsMatched.value.push({
    fileColumn: Object.keys(props.columns)[columnsMatched.value.length],
    schemaColumn: 'choose',
  })
}

function onFileColumnChanged(columnIndex: number, columnName: string | undefined) {
  columnsMatched.value = columnsMatched.value.map((column, index) => {
    if (index === columnIndex) {
      column.fileColumn = columnName
    }

    return column
  })
}

function onSchemaColumnChanged(columnIndex: number, columnValue: string | undefined) {
  columnsMatched.value = columnsMatched.value.map((column, index) => {
    if (index === columnIndex) {
      column.schemaColumn = columnValue
    }

    return column
  })
}

function onRemoveColumn(columnIndex: number) {
  columnsMatched.value = columnsMatched.value.filter((_, index) => index !== columnIndex)
}

onMounted(() => {
  columnsMatched.value = [
    {
      fileColumn: Object.keys(props.columns)[0],
      schemaColumn: schemaColumns.value[0].value,
    },
  ]
})

watch(
  columnsMatched,
  (newColumnsMatched) => {
    emit(
      'update:modelValue',
      Object.values(newColumnsMatched).reduce((acc, column) => {
        if (column.fileColumn && column.schemaColumn) {
          acc[column.fileColumn] = column.schemaColumn
        }

        return acc
      }, {} as Record<string, string>),
    )
  },
)
</script>

<template>
  <div class="h-fit select-none rounded-lg text-left text-base dark:border-gray-800">
    <div class="hidden flex-col items-center border-b border-gray-200 md:flex md:flex-row dark:border-gray-800">
      <div
        v-for="header of headers"
        :key="header.value"
        class="border-r border-gray-200 p-4 text-sm font-semibold leading-snug text-gray-800 last:border-0 dark:border-gray-800 dark:text-gray-200"
        :class="{
          'min-w-52 flex-1 text-center': header.value === 'preview',
          'w-52 min-w-52 flex-initial text-left': header.value !== 'preview' && header.value !== 'actions',
          'w-16 min-w-16': header.value === 'actions',
        }"
      >
        {{ header.label }}
      </div>
    </div>

    <div v-if="Object.values(columnsMatched).length > 0">
      <template
        v-for="(column, columnIndex) of columnsMatched"
        :key="columnIndex"
      >
        <FieldColumnsMatcherRow
          class="border-b border-gray-200 last:border-0 last-of-type:rounded-b-xl dark:border-gray-800"
          :class="{
            'bg-gray-100 dark:bg-gray-900': columnIndex % 2 === 0,
            'bg-white dark:bg-gray-950': columnIndex % 2 === 1,
          }"
          :column-names="Object.keys(columns)"
          :column-preview="column.fileColumn ? columns[column.fileColumn] : []"
          :file-column="column.fileColumn"
          :headers="headers"
          :schema-column="column.schemaColumn"
          :schema-columns="schemaColumns"
          @update:file-column="onFileColumnChanged(columnIndex, $event)"
          @update:schema-column="onSchemaColumnChanged(columnIndex, $event)"
        >
          <template #actions>
            <UTooltip :text="columnsMatched.length > 1 ? '' : t('buttons.removeColumnTooltip')">
              <UButton
                :disabled="!(columnsMatched.length > 1)"
                :icon="getIcon('trash')"
                :ui="{ icon: { base: 'text-red-700 dark:text-red-300' } }"
                @click="onRemoveColumn(columnIndex)"
              />
            </UTooltip>
          </template>
        </FieldColumnsMatcherRow>
      </template>
    </div>

    <div
      v-if="columnsMatched.length < Object.keys(columns).length"
      class="flex h-16 items-center justify-start gap-2 border-t border-gray-200 p-4 dark:border-gray-800"
    >
      <UButton
        :disabled="columnsMatched.length >= Object.keys(columns).length"
        :icon="getIcon('add')"
        :label="t('buttons.moreColumns')"
        :ui="{ icon: { base: 'text-blue-800 dark:text-blue-200' } }"
        @click="onMoreColumns"
      />
    </div>
  </div>
</template>

<i18n lang="json">
{
  "en": {
    "buttons": {
      "moreColumns": "Match more columns",
      "moreColumnsTooltip": "There is no more columns to match.",
      "removeColumn": "Remove column",
      "removeColumnTooltip": "You must at least match 1 column."
    },
    "headers": {
      "actions": "",
      "match": "Our columns",
      "name": "File columns",
      "preview": "Preview"
    },
    "options": {
      "choose": "Choose a column",
      "domain": "Domain / Website",
      "dribbble": "Dribbble",
      "facebook": "Facebook",
      "github": "GitHub",
      "instagram": "Instagram",
      "linkedin": "LinkedIn",
      "name": "Company name",
      "pinterest": "Pinterest",
      "snapchat": "Snapchat",
      "tiktok": "TikTok",
      "twitter": "Twitter",
      "youtube": "YouTube"
    }
  },
  "es": {
    "buttons": {
      "moreColumns": "Asociar más columnas",
      "moreColumnsTooltip": "No puedes asociar más columnas.",
      "removeColumn": "Eliminar columna",
      "removeColumnTooltip": "Debes asociar al menos 1 columna."
    },
    "headers": {
      "actions": "",
      "match": "Nuestras columnas",
      "name": "Columnas del archivo",
      "preview": "Vista previa"
    },
    "options": {
      "choose": "Elegir una columna",
      "domain": "Dominio / Website",
      "dribbble": "Dribbble",
      "facebook": "Facebook",
      "github": "GitHub",
      "instagram": "Instagram",
      "linkedin": "LinkedIn",
      "name": "Nombre de la empresa",
      "pinterest": "Pinterest",
      "snapchat": "Snapchat",
      "tiktok": "TikTok",
      "twitter": "Twitter",
      "youtube": "YouTube"
    }
  },
  "fr": {
    "buttons": {
      "moreColumns": "Associer plus de colonnes",
      "moreColumnsTooltip": "Il n'y a pas plus de colonnes à associer.",
      "removeColumn": "Supprimer une colonne",
      "removeColumnTooltip": "Vous devez associer au moins 1 colonne."
    },
    "headers": {
      "actions": "",
      "match": "Nos colonnes",
      "name": "Colonnes fichier",
      "preview": "Aperçu"
    },
    "options": {
      "choose": "Choisir une colonne",
      "domain": "Domaine / Website",
      "dribbble": "Dribbble",
      "facebook": "Facebook",
      "github": "GitHub",
      "instagram": "Instagram",
      "linkedin": "LinkedIn",
      "name": "Nom d'entreprise",
      "pinterest": "Pinterest",
      "snapchat": "Snapchat",
      "tiktok": "TikTok",
      "twitter": "Twitter",
      "youtube": "YouTube"
    }
  }
}
</i18n>
